/* user-flow-gate.js
 *
 * Returning-user urgency flow — STEP 2: the flow gate.
 *
 * Reads the purchase signal published by purchase-info.js (step 1) plus the
 * landing-URL campaign markers, and resolves a single switch:
 *
 *     window.__userFlow  ∈  'new-user' | 'returning-user' | 'campaign'
 *
 * plus a richer window.__userFlowDetail and a `SE_userFlowReady` event. The
 * engine (step 3) reads __userFlow to pick its config profile. Kept SEPARATE
 * from purchase-info.js so the fetch stays a pure data module and the routing
 * stays pure + independently testable.
 *
 * Pure client-side: decides at page load from data already in the browser — no
 * backend round-trip. Fail-safe: anything but a CONFIRMED buyer → 'new-user'.
 *
 * Campaign carve-out — IMPLEMENTED BUT BYPASSED (detect-but-don't-act):
 *   • DETECTION runs by default: window.AGB_CAMPAIGN_SIGNATURE (default ['drip'])
 *     is the list of utm_campaign/source/medium substrings marking the
 *     post-delivery campaign. Discovered 2026-06-01 from BigQuery
 *     cae_events.utm_source — the ESP is Drip; 'package_insert' (physical insert
 *     QR) is also post-delivery. So window.__userFlowDetail.isCampaign is
 *     observable right now (console / telemetry) without changing behaviour.
 *   • EFFECT is gated by window.AGB_CAMPAIGN_CARVEOUT (default FALSE = bypass):
 *     while bypassed, a detected campaign arrival is FLAGGED but STILL resolves
 *     to 'returning-user' (it sees the workflow). Flip AGB_CAMPAIGN_CARVEOUT=true
 *     to route campaign arrivals to 'campaign' — no code change.
 *   • CAVEAT before flipping: 'drip' matches ALL Drip emails (newsletters/promos),
 *     not just post-delivery. Narrow AGB_CAMPAIGN_SIGNATURE to the specific
 *     post-delivery utm_campaign (SELECT utm_campaign … WHERE utm_source='drip')
 *     before enabling the effect.
 */
(function () {
  'use strict';

  function campaignSignature() {
    return (typeof window !== 'undefined' && Array.isArray(window.AGB_CAMPAIGN_SIGNATURE))
      ? window.AGB_CAMPAIGN_SIGNATURE
      : ['drip']; // detection ON (discovered 2026-06-01); narrow to the post-delivery utm_campaign before enabling the effect
  }

  // Master switch for the carve-out EFFECT. Default false = BYPASSED: campaign
  // arrivals are detected + flagged but still resolve to 'returning-user'.
  function carveOutEnabled() {
    return (typeof window !== 'undefined') && window.AGB_CAMPAIGN_CARVEOUT === true;
  }

  // DELIVERED gate. The returning-user workflow launches only for a buyer whose
  // order has been DELIVERED (post-delivery), per SE_purchaseInfo.delivered (set
  // by /api/visitor/orders from the Katana sales_order mirror). Default ON
  // (enforce); set window.AGB_REQUIRE_DELIVERED = false to BYPASS (returning
  // launches regardless of delivery). The panel can drive the global via
  // theme.liquid.
  function requireDelivered() {
    return (typeof window === 'undefined') || window.AGB_REQUIRE_DELIVERED !== false;
  }

  // True only when the CURRENT landing URL carries one of the configured
  // campaign markers (utm_campaign / utm_source / utm_medium).
  function arrivedViaCampaign() {
    var sig = campaignSignature();
    if (!sig.length) return false;
    var fields;
    try {
      var qs = new URLSearchParams(window.location.search);
      fields = [qs.get('utm_campaign'), qs.get('utm_source'), qs.get('utm_medium')]
        .filter(Boolean)
        .map(function (s) { return String(s).toLowerCase(); });
    } catch (e) { return false; }
    // NOTE: reads the LIVE landing URL only. When narrowing the campaign branch,
    // consider also reading CAE's first-touch attribution so the tag survives
    // internal navigation (confirm CAE's exposed shape first).
    return fields.some(function (v) {
      return sig.some(function (s) { return v.indexOf(String(s).toLowerCase()) !== -1; });
    });
  }

  // Returns { flow, isCampaign, bypassed }.
  function decide(info) {
    if (!info || !info.has_purchased) {
      return { flow: 'new-user', isCampaign: false, bypassed: false };
    }
    // DELIVERED gate: a CONFIRMED not-yet-delivered buyer (delivered === false)
    // does NOT get the returning workflow — they fall back to new-user. We block
    // ONLY on an explicit false; `true` and `null` (cache still warming) proceed,
    // so a genuinely-delivered buyer isn't penalised on first load while the
    // background DELIVERED probe resolves. Bypass via window.AGB_REQUIRE_DELIVERED=false.
    if (requireDelivered() && info.delivered === false) {
      return { flow: 'new-user', isCampaign: false, bypassed: false, _reason: 'not-delivered' };
    }
    var isCampaign = arrivedViaCampaign();
    if (isCampaign && carveOutEnabled()) {
      return { flow: 'campaign', isCampaign: true, bypassed: false };
    }
    // Organic buyer, OR a campaign arrival while the carve-out is bypassed
    // (bypassed=true means "detected as campaign but intentionally not acted on").
    return { flow: 'returning-user', isCampaign: isCampaign, bypassed: isCampaign };
  }

  var resolved = false;
  function resolve(info, source) {
    if (resolved) return;
    resolved = true;
    var d = decide(info);
    window.__userFlow = d.flow;
    window.__userFlowDetail = {
      flow: d.flow, isCampaign: d.isCampaign, bypassed: d.bypassed,
      source: source || null, info: info || null
    };
    try {
      window.dispatchEvent(new CustomEvent('SE_userFlowReady', { detail: window.__userFlowDetail }));
    } catch (e) {}
    try {
      if (window.theme && window.theme.scarcityMode) {
        console.log('[user-flow-gate] __userFlow =', d.flow,
          (d.bypassed ? '(campaign DETECTED but carve-out BYPASSED)' : ''),
          '(via ' + (source || '?') + ')', info);
      }
    } catch (e) {}
  }

  // purchase-info.js sets window.SE_purchaseInfo + fires SE_purchaseInfoReady
  // with `detail.info`. Handle either ordering (the event may already have
  // fired by the time this module's IIFE runs).
  if (window.SE_purchaseInfo) {
    resolve(window.SE_purchaseInfo, 'already-published');
  } else {
    window.addEventListener('SE_purchaseInfoReady', function (e) {
      var info = (e && e.detail && e.detail.info) || window.SE_purchaseInfo;
      resolve(info, (e && e.detail && e.detail.source) || 'event');
    }, { once: true });
    // Backstop only: step 1 fail-safes to {known:false} (which fires the
    // event), so this fires solely if purchase-info.js is absent / never loads.
    setTimeout(function () {
      resolve(window.SE_purchaseInfo || { known: false, has_purchased: false, _reason: 'gate-timeout' }, 'timeout');
    }, 12000);
  }
})();
